import React from "react";
import { Box, Typography, Alert, Button } from "@mui/material";
import { ErrorOutline, Home, Login } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { AuthManageLayout, AuthH1 } from "./shared";

/**
 * SocialAuthenticationError - Error component replacing socialaccount/authentication_error.html
 *
 * This component handles social authentication errors with:
 * - Clear error messaging
 * - Navigation options back to login or home
 * - Professional error display
 * - Integration with existing app layout
 */

interface SocialAuthenticationErrorProps {
  /** Custom error message */
  errorMessage?: string;
  /** Error code for debugging */
  errorCode?: string;
  /** Provider name that failed */
  providerName?: string;
}

const SocialAuthenticationError: React.FC<SocialAuthenticationErrorProps> = ({
  errorMessage,
  errorCode,
  providerName,
}) => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate("/");
  };

  const handleGoToLogin = () => {
    navigate("/auth/login");
  };

  const defaultErrorMessage = providerName
    ? `An error occurred while attempting to login via ${providerName}.`
    : "An error occurred while attempting to login via your social network account.";

  return (
    <AuthManageLayout title="Authentication Error">
      <Box sx={{ textAlign: "center" }}>
        <Box sx={{ mb: 3 }}>
          <ErrorOutline sx={{ fontSize: 64, color: "statusText.attention", mb: 2 }} />
        </Box>

        <AuthH1>Social Network Login Failure</AuthH1>

        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          {errorMessage || defaultErrorMessage}
        </Typography>

        {/* Error Details */}
        <Alert severity="error" sx={{ mb: 4, textAlign: "left" }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>What happened?</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The authentication with your social provider was not successful.
            This could be due to:
          </Typography>
          <Box component="ul" sx={{ textAlign: "left", pl: 2, mb: 0 }}>
            <li>The authentication was cancelled</li>
            <li>Invalid or expired social provider credentials</li>
            <li>Network connectivity issues</li>
            <li>Temporary service unavailability</li>
          </Box>

          {errorCode && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2, fontFamily: "monospace" }}
            >
              Error Code: {errorCode}
            </Typography>
          )}
        </Alert>

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="contained"
            startIcon={<Login />}
            onClick={handleGoToLogin}
            size="large"
          >
            Back to Login
          </Button>

          <Button
            variant="outlined"
            startIcon={<Home />}
            onClick={handleGoHome}
            size="large"
          >
            Go Home
          </Button>
        </Box>

        {/* Help Text */}
        <Box sx={{ mt: 4, p: { xs: 2, sm: 3 }, bgcolor: "surfaces.surfaceRaised", borderRadius: 1 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Need Help?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            If you continue to experience issues with social authentication,
            try:
          </Typography>
          <Box component="ul" sx={{ textAlign: "left", pl: 2, mb: 0 }}>
            <li>Signing in with your email and password instead</li>
            <li>Clearing your browser cookies and trying again</li>
            <li>Checking that third-party cookies are enabled</li>
            <li>Contacting support if the problem persists</li>
          </Box>
        </Box>
      </Box>
    </AuthManageLayout>
  );
};

export default SocialAuthenticationError;
